import { LogOut, Settings as SettingsIcon, Sparkles } from 'lucide-react';

import { mainNav } from '@/components/layout/AppSidebar';
import unifyLogo from '@/assets/unify-logo.png';

/**
 * A non-interactive preview of the app sidebar shown alongside the welcome
 * hero. It hints at what's waiting behind "Get started" without being usable —
 * the whole column is `pointer-events-none` and slightly dimmed, and the first
 * item is highlighted to suggest where the retailer lands next.
 */
export function WelcomeNavHint({ shopName }) {
	const initials = (shopName ?? 'Your shop')
		.split(' ')
		.map((p) => p[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();

	return (
		<aside
			aria-hidden
			className="pointer-events-none hidden h-screen w-[248px] shrink-0 select-none flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground opacity-70 lg:flex"
		>
			<div className="mx-4 mb-6 mt-7 flex items-center gap-3 rounded-2xl border border-sidebar-border bg-white px-3 py-2.5">
				<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
					{initials}
				</div>
				<div className="min-w-0">
					<div className="truncate text-sm font-semibold text-foreground">{shopName ?? 'Your shop'}</div>
				</div>
			</div>

			<div className="group relative mx-4 -mt-2 mb-4 flex w-[calc(100%-2rem)] items-center gap-3 rounded-xl bg-white px-3.5 py-2.5 text-sm font-medium text-foreground shadow-unify">
				<span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-gradient-unify shadow-sm">
					<Sparkles className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
				</span>
				<span>Ask AI</span>
			</div>

			<nav className="flex-1 space-y-1 px-3">
				{mainNav.map((item, index) => {
					const Icon = item.icon;
					return (
						<div
							key={item.to}
							className={
								index === 0
									? 'flex items-center gap-3 rounded-xl bg-sidebar-accent px-3.5 py-2.5 text-sm font-semibold text-sidebar-accent-foreground'
									: 'flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-sidebar-foreground'
							}
						>
							<Icon className="h-[18px] w-[18px]" strokeWidth={2} />
							<span>{item.label}</span>
						</div>
					);
				})}
			</nav>

			<div className="p-3">
				<div className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-sidebar-foreground">
					<SettingsIcon className="h-[18px] w-[18px]" />
					<span>Settings</span>
				</div>
				<div className="mt-1 flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-sidebar-foreground">
					<LogOut className="h-[18px] w-[18px]" />
					<span>Sign out</span>
				</div>
			</div>

			<div className="px-6 pb-2 pt-4">
				<img src={unifyLogo} alt="Unify by NearSt" className="h-20 w-auto" />
			</div>
		</aside>
	);
}
